import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getAdminStats,
  getAdminUsers,
  updateAdminUser,
  deleteAdminUser,
  resetAdminUserFinancialData,
  getAccessLogs,
  deleteAccessLog,
  clearAccessLogs,
  getAdminSystemStatus,
  getAdminAuditLogs,
  getAdminLoginFailures,
  blockAdminIp,
  unblockAdminIp,
  deleteAdminLoginFailure,
  clearAdminLoginFailures,
  getAdminImports,
  getAdminBackups,
  createAdminBackup,
  updateAdminMaintenance,
  getAdminExchangeRates,
  refreshAdminExchangeRates,
  getAdminOpenAIBilling
} from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { 
  Users, Database, Activity, Clock, Shield, Eye, 
  UserCheck, UserX, LogOut, LayoutDashboard, ChevronRight, Loader2, Trash2, Menu, X, Receipt, Settings,
  Key, Plus, Copy, Check, AlertTriangle, RefreshCw, DollarSign
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const AdminPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loginFailures, setLoginFailures] = useState({ last_24h: 0, by_source: [], recent: [] });
  const [importLogs, setImportLogs] = useState([]);
  const [backups, setBackups] = useState([]);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [openAIBilling, setOpenAIBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [ipActionLoading, setIpActionLoading] = useState('');
  const [loginFailureActionLoading, setLoginFailureActionLoading] = useState('');
  const [adminMessage, setAdminMessage] = useState('');
  const [adminError, setAdminError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [statusActionUserId, setStatusActionUserId] = useState(null);
  const [userActionMessage, setUserActionMessage] = useState('');
  const [userActionError, setUserActionError] = useState('');
  const [logActionMessage, setLogActionMessage] = useState('');
  const [logActionError, setLogActionError] = useState('');
  const [logActionLoading, setLogActionLoading] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusTargetUser, setStatusTargetUser] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // Invitation system state
  const [invitationCodes, setInvitationCodes] = useState([]);
  const [systemSettings, setSystemSettings] = useState({ registration_open: true, require_invitation: false, allow_user_financial_reset: true });
  const [newCodeDialogOpen, setNewCodeDialogOpen] = useState(false);
  const [newCodeForm, setNewCodeForm] = useState({ code: '', max_uses: 1, expires_days: '', note: '' });
  const [copiedCode, setCopiedCode] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsData, usersData, logsData, statusData, auditData, failuresData, importsData, backupsData, exchangeRatesData, openAIBillingData] = await Promise.all([
        getAdminStats(),
        getAdminUsers(),
        getAccessLogs(30),
        getAdminSystemStatus(),
        getAdminAuditLogs(80),
        getAdminLoginFailures(80),
        getAdminImports(80),
        getAdminBackups(),
        getAdminExchangeRates(),
        getAdminOpenAIBilling().catch((error) => ({
          status: 'error',
          message: error.response?.data?.detail || 'No fue posible consultar OpenAI'
        }))
      ]);
      setStats(statsData);
      setUsers(usersData);
      setAccessLogs(logsData);
      setSystemStatus(statusData);
      setAuditLogs(auditData);
      setLoginFailures(failuresData);
      setImportLogs(importsData);
      setBackups(backupsData);
      setExchangeRates(exchangeRatesData);
      setOpenAIBilling(openAIBillingData);
      
      // Fetch invitation codes and settings
      try {
        const [codesRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/invitation-codes`, { headers }),
          fetch(`${API_URL}/api/admin/settings`, { headers })
        ]);
        if (codesRes.ok) setInvitationCodes(await codesRes.json());
        if (settingsRes.ok) setSystemSettings(await settingsRes.json());
      } catch (e) { console.log('Invitation system not available'); }
      
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openStatusDialog = (targetUser) => {
    setStatusTargetUser(targetUser);
    setStatusReason('');
    setUserActionMessage('');
    setUserActionError('');
    setStatusDialogOpen(true);
  };

  const handleToggleUserStatus = async () => {
    if (!statusTargetUser) return;
    const targetUser = statusTargetUser;
    const nextActive = !targetUser.is_active;
    const actionLabel = nextActive ? 'desbloquear' : 'bloquear';

    try {
      setStatusActionUserId(targetUser.id);
      setUserActionMessage('');
      setUserActionError('');
      await updateAdminUser(targetUser.id, {
        is_active: nextActive,
        status_reason: statusReason.trim()
      });
      await fetchData();
      setUserActionMessage(`Usuario ${actionLabel === 'bloquear' ? 'bloqueado' : 'desbloqueado'} correctamente: ${targetUser.email}`);
      setStatusDialogOpen(false);
      setStatusTargetUser(null);
      setStatusReason('');
    } catch (error) {
      console.error('Error updating user:', error);
      setUserActionError(error.response?.data?.detail || `No fue posible ${actionLabel} el usuario`);
    } finally {
      setStatusActionUserId(null);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`¿Estás seguro de eliminar al usuario ${userEmail}? Esta acción eliminará también todas sus transacciones y no se puede deshacer.`)) {
      try {
        await deleteAdminUser(userId);
        fetchData();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error al eliminar usuario: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openResetDialog = (targetUser) => {
    setResetTargetUser(targetUser);
    setResetConfirmation('');
    setUserActionMessage('');
    setUserActionError('');
    setResetDialogOpen(true);
  };

  const handleResetFinancialData = async () => {
    if (!resetTargetUser) return;
    try {
      setResetLoading(true);
      setUserActionMessage('');
      setUserActionError('');
      const result = await resetAdminUserFinancialData(resetTargetUser.id, {
        confirmation: resetConfirmation,
        restore_system_categories: true
      });
      await fetchData();
      setUserActionMessage(`Datos financieros reiniciados para ${resetTargetUser.email}. Transacciones eliminadas: ${result.deleted?.transactions || 0}`);
      setResetDialogOpen(false);
      setResetTargetUser(null);
      setResetConfirmation('');
    } catch (error) {
      console.error('Error resetting financial data:', error);
      setUserActionError(error.response?.data?.detail || 'No fue posible reiniciar los datos financieros');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteAccessLog = async (log) => {
    if (!log?.id) return;
    if (!window.confirm('¿Eliminar este registro de acceso?')) return;

    try {
      setLogActionLoading(log.id);
      setLogActionMessage('');
      setLogActionError('');
      await deleteAccessLog(log.id);
      await fetchData();
      setLogActionMessage('Registro de acceso eliminado');
    } catch (error) {
      console.error('Error deleting access log:', error);
      setLogActionError(error.response?.data?.detail || 'No fue posible eliminar el registro');
    } finally {
      setLogActionLoading('');
    }
  };

  const handleClearAccessLogs = async () => {
    if (!window.confirm('¿Eliminar todos los registros de acceso? Esta acción no se puede deshacer.')) return;

    try {
      setLogActionLoading('all');
      setLogActionMessage('');
      setLogActionError('');
      await clearAccessLogs();
      await fetchData();
      setLogActionMessage('Registros de acceso eliminados');
    } catch (error) {
      console.error('Error clearing access logs:', error);
      setLogActionError(error.response?.data?.detail || 'No fue posible limpiar los registros');
    } finally {
      setLogActionLoading('');
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('¿Crear un backup manual ahora?')) return;
    try {
      setBackupLoading(true);
      setAdminMessage('');
      setAdminError('');
      await createAdminBackup();
      await fetchData();
      setAdminMessage('Backup creado');
    } catch (error) {
      console.error('Error creating backup:', error);
      setAdminError(error.response?.data?.detail || 'No fue posible crear el backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleMaintenanceToggle = async (value) => {
    try {
      setAdminMessage('');
      setAdminError('');
      await updateAdminMaintenance({ maintenance_mode: value });
      setSystemStatus(prev => ({ ...(prev || {}), maintenance_mode: value }));
      await fetchData();
      setAdminMessage(value ? 'Modo mantenimiento activado' : 'Modo mantenimiento desactivado');
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      setAdminError(error.response?.data?.detail || 'No fue posible actualizar mantenimiento');
    }
  };

  const handleRefreshExchangeRates = async () => {
    try {
      setExchangeRateLoading(true);
      setAdminMessage('');
      setAdminError('');
      const result = await refreshAdminExchangeRates();
      await fetchData();
      setAdminMessage(result.updated ? 'Cotizaciones actualizadas correctamente' : 'Cotizaciones revisadas; se mantiene la última tasa disponible');
    } catch (error) {
      console.error('Error refreshing exchange rates:', error);
      setAdminError(error.response?.data?.detail || 'No fue posible actualizar las cotizaciones');
    } finally {
      setExchangeRateLoading(false);
    }
  };

  const handleToggleBlockedIp = async (item) => {
    if (!item?.ip_address || item.ip_address === 'unknown') return;
    const shouldUnblock = !!item.is_blocked;
    const confirmMessage = shouldUnblock
      ? `¿Desbloquear la IP ${item.ip_address}?`
      : `¿Bloquear la IP ${item.ip_address} para nuevos logins y registros?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      setIpActionLoading(item.ip_address);
      setAdminMessage('');
      setAdminError('');
      if (shouldUnblock) {
        await unblockAdminIp(item.ip_address);
        setAdminMessage('IP desbloqueada');
      } else {
        await blockAdminIp({
          ip_address: item.ip_address,
          reason: `Intentos fallidos: ${item.count || 0}`,
        });
        setAdminMessage('IP bloqueada');
      }
      await fetchData();
    } catch (error) {
      console.error('Error updating blocked IP:', error);
      setAdminError(error.response?.data?.detail || 'No fue posible actualizar la IP');
    } finally {
      setIpActionLoading('');
    }
  };

  const handleDeleteLoginFailure = async (log) => {
    if (!log?.id) return;
    if (!window.confirm('¿Borrar este intento fallido de login?')) return;
    try {
      setLoginFailureActionLoading(log.id);
      setAdminMessage('');
      setAdminError('');
      await deleteAdminLoginFailure(log.id);
      await fetchData();
      setAdminMessage('Intento fallido eliminado');
    } catch (error) {
      console.error('Error deleting failed login:', error);
      setAdminError(error.response?.data?.detail || 'No fue posible borrar el intento fallido');
    } finally {
      setLoginFailureActionLoading('');
    }
  };

  const handleClearLoginFailures = async () => {
    if (!window.confirm('¿Borrar todos los intentos fallidos de login? Esta acción no se puede deshacer.')) return;
    try {
      setLoginFailureActionLoading('all');
      setAdminMessage('');
      setAdminError('');
      await clearAdminLoginFailures();
      await fetchData();
      setAdminMessage('Intentos fallidos eliminados');
    } catch (error) {
      console.error('Error clearing failed logins:', error);
      setAdminError(error.response?.data?.detail || 'No fue posible borrar los intentos fallidos');
    } finally {
      setLoginFailureActionLoading('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      setSystemSettings(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/invitation-codes`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: newCodeForm.code || null,
          max_uses: parseInt(newCodeForm.max_uses) || 1,
          expires_days: newCodeForm.expires_days ? parseInt(newCodeForm.expires_days) : null,
          note: newCodeForm.note || null
        })
      });
      if (response.ok) {
        setNewCodeDialogOpen(false);
        setNewCodeForm({ code: '', max_uses: 1, expires_days: '', note: '' });
        fetchData();
      }
    } catch (error) {
      console.error('Error creating code:', error);
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('¿Eliminar este código de invitación?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/invitation-codes/${codeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting code:', error);
    }
  };

  const handleToggleCode = async (codeId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/invitation-codes/${codeId}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling code:', error);
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleString('es-CO', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes || 0);
    if (value >= 1024 * 1024 * 1024) return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
    if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${value} B`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  // Prepare chart data
  const collectionsData = stats?.collections ? 
    Object.entries(stats.collections).map(([name, count]) => ({ name, count })) : [];

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-zinc-900 border-r border-zinc-800 p-6 z-50 overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Admin</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-400 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-2">
              <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <LayoutDashboard className="h-5 w-5" />
                Dashboard
              </Link>
              <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Receipt className="h-5 w-5" />
                Transacciones
              </Link>
              <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Settings className="h-5 w-5" />
                Configuración
              </Link>
              <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20" onClick={() => setMobileMenuOpen(false)}>
                <Shield className="h-5 w-5" />
                Admin Panel
              </Link>
            </nav>
            <div className="mt-6 border-t border-zinc-800 pt-5">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-violet-400" />
                  <span className="text-xs font-medium text-violet-400 uppercase">Admin</span>
                </div>
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:flex lg:flex-col min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Admin</p>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors"
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="h-5 w-5" />
            Dashboard
          </Link>
          <Link 
            to="/admin" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20"
            data-testid="nav-admin"
          >
            <Shield className="h-5 w-5" />
            Admin Panel
          </Link>
        </nav>

        <div className="mt-6 border-t border-zinc-800 pt-5">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-violet-400" />
              <span className="text-xs font-medium text-violet-400 uppercase">Admin</span>
            </div>
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-zinc-400 hover:text-red-400"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="flex items-center gap-4 mb-8">
          <button 
            className="lg:hidden text-zinc-400 hover:text-white"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-white">Panel de Administración</h2>
            <p className="text-zinc-400">Gestiona usuarios y monitorea el sistema</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="stat-card border-zinc-800 card-hover" data-testid="stat-total-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Total Usuarios</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-violet-500/10">
                  <Users className="h-6 w-6 text-violet-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-zinc-800 card-hover" data-testid="stat-active-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Usuarios Activos</p>
                  <p className="text-3xl font-bold text-emerald-400">{stats?.active_users || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10">
                  <UserCheck className="h-6 w-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-zinc-800 card-hover" data-testid="stat-transactions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Transacciones</p>
                  <p className="text-3xl font-bold text-blue-400">{stats?.total_transactions || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-500/10">
                  <Activity className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card border-zinc-800 card-hover" data-testid="stat-logins-24h">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm mb-1">Logins (24h)</p>
                  <p className="text-3xl font-bold text-amber-400">{stats?.recent_logins_24h || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-500/10">
                  <Clock className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Database Stats Chart */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-8" data-testid="database-chart">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="h-5 w-5 text-violet-400" />
              Datos en Base de Datos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 min-h-[256px]">
              {collectionsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={collectionsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="name" stroke="#71717A" tick={{ fill: '#A1A1AA' }} />
                  <YAxis stroke="#71717A" tick={{ fill: '#A1A1AA' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181B', 
                      border: '1px solid #27272A',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" name="Registros" fill="#7C3AED" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-500">
                  No hay datos
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {adminMessage && (
          <div className="mb-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
            {adminMessage}
          </div>
        )}
        {adminError && (
          <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {adminError}
          </div>
        )}

        {/* Tabs for Admin Tools */}
        <Tabs defaultValue="control" className="space-y-6">
          <TabsList className="bg-zinc-800/50 flex flex-wrap h-auto justify-start">
            <TabsTrigger value="control" data-testid="tab-control">Centro de Control</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="tab-invitations">
              <Key className="h-4 w-4 mr-2" />
              Invitaciones
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Auditoría</TabsTrigger>
            <TabsTrigger value="imports" data-testid="tab-imports">Importaciones</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">Últimos Accesos</TabsTrigger>
          </TabsList>

          <TabsContent value="control">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800 xl:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-400" />
                    Estado del Sistema
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">API</p>
                    <p className="text-lg font-semibold text-emerald-300">{systemStatus?.api || 'online'}</p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">Base de datos</p>
                    <p className={systemStatus?.database === 'ok' ? 'text-lg font-semibold text-emerald-300' : 'text-lg font-semibold text-red-300'}>
                      {systemStatus?.database || 'ok'}
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">Disco usado</p>
                    <p className="text-lg font-semibold text-white">{systemStatus?.disk?.used_percent ?? 0}%</p>
                    <p className="text-xs text-zinc-500">
                      Libre {formatBytes(systemStatus?.disk?.free_bytes)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">Intentos fallidos 24h</p>
                    <p className="text-lg font-semibold text-amber-300">{loginFailures?.last_24h ?? systemStatus?.failed_logins_24h ?? 0}</p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 md:col-span-2">
                    <p className="text-sm text-zinc-400">Último backup</p>
                    <p className="text-white">
                      {systemStatus?.last_backup ? `${systemStatus.last_backup.filename} · ${formatDate(systemStatus.last_backup.created_at)}` : 'No registrado'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Database className="h-5 w-5 text-sky-400" />
                    Seguridad
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-zinc-300">Modo mantenimiento</p>
                      <p className="text-xs text-zinc-500">Marca el sistema para intervención administrativa</p>
                    </div>
                    <Switch
                      checked={!!systemStatus?.maintenance_mode}
                      onCheckedChange={handleMaintenanceToggle}
                    />
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm text-zinc-400">
                    Registro: {systemStatus?.registration_open ? 'abierto' : 'cerrado'} · Invitación: {systemStatus?.require_invitation ? 'sí' : 'no'}
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm text-zinc-400">
                    Reset usuario: {systemStatus?.allow_user_financial_reset !== false ? 'permitido' : 'bloqueado'}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800 xl:col-span-2">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-emerald-400" />
                      Cotizaciones
                    </CardTitle>
                    <p className="text-sm text-zinc-400">Frankfurter se consulta automáticamente una vez por día.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshExchangeRates}
                    disabled={exchangeRateLoading}
                    className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                  >
                    {exchangeRateLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Actualizar ahora
                  </Button>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">Proveedor</p>
                    <p className="text-lg font-semibold text-white">{exchangeRates?.latest?.provider || 'frankfurter'}</p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">Última consulta</p>
                    <p className="text-sm font-semibold text-white">{formatDate(exchangeRates?.latest?.fetched_at)}</p>
                    <p className="text-xs text-zinc-500">Fecha tasa: {exchangeRates?.latest?.date || '-'}</p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">USD → BRL</p>
                    <p className="text-lg font-semibold text-emerald-300">
                      {exchangeRates?.latest?.raw_rates?.BRL ? exchangeRates.latest.raw_rates.BRL.toFixed(4) : 'No disponible'}
                    </p>
                    <p className="text-xs text-zinc-500">Guardado en base de datos</p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 md:col-span-3">
                    <p className="text-sm text-zinc-400">Monedas automáticas</p>
                    <p className="text-sm text-zinc-200">
                      {(exchangeRates?.supported_by_provider || []).join(', ') || 'USD, BRL, EUR, GBP, MXN'}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Otras monedas usan fallback hasta que agreguemos un segundo proveedor o tasa manual.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="h-5 w-5 text-violet-400" />
                    OpenAI API
                  </CardTitle>
                  <p className="text-sm text-zinc-400">Costos del mes y saldo estimado para recarga.</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4">
                    <p className="text-sm text-zinc-400">Gasto este mes</p>
                    <p className="text-2xl font-semibold text-white">
                      {openAIBilling?.month_cost_usd != null ? `$${Number(openAIBilling.month_cost_usd).toFixed(2)}` : 'No disponible'}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {openAIBilling?.fetched_at ? `Actualizado ${formatDate(openAIBilling.fetched_at)}` : openAIBilling?.message || 'Esperando configuración'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
                      <p className="text-xs text-zinc-500">Presupuesto</p>
                      <p className="text-sm font-semibold text-zinc-200">
                        {openAIBilling?.budget_usd != null ? `$${Number(openAIBilling.budget_usd).toFixed(2)}` : 'No definido'}
                      </p>
                    </div>
                    <div className={`rounded-lg border p-3 ${
                      openAIBilling?.needs_recharge
                        ? 'border-amber-500/40 bg-amber-500/10'
                        : 'border-zinc-800 bg-zinc-950/50'
                    }`}>
                      <p className="text-xs text-zinc-500">Restante estimado</p>
                      <p className={openAIBilling?.needs_recharge ? 'text-sm font-semibold text-amber-300' : 'text-sm font-semibold text-emerald-300'}>
                        {openAIBilling?.estimated_remaining_usd != null ? `$${Number(openAIBilling.estimated_remaining_usd).toFixed(2)}` : 'No calculado'}
                      </p>
                    </div>
                  </div>
                  {openAIBilling?.needs_recharge && (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                      Saldo estimado bajo. Conviene recargar OpenAI.
                    </div>
                  )}
                  {openAIBilling?.status && openAIBilling.status !== 'ok' && (
                    <div className="rounded-md border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-400">
                      {openAIBilling.status === 'not_configured'
                        ? 'Configura OPENAI_ADMIN_API_KEY para consultar costos oficiales.'
                        : openAIBilling.message || 'No fue posible consultar costos de OpenAI.'}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800 xl:col-span-3">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Shield className="h-5 w-5 text-amber-400" />
                      Intentos de Login Fallidos
                    </CardTitle>
                    <p className="text-sm text-zinc-400">Origen de los intentos fallidos registrados en las últimas 24 horas.</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearLoginFailures}
                    disabled={!loginFailures?.recent?.length || loginFailureActionLoading === 'all'}
                    className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                  >
                    {loginFailureActionLoading === 'all' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Borrar todos
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400">IP</TableHead>
                        <TableHead className="text-zinc-400">País</TableHead>
                        <TableHead className="text-zinc-400">Intentos</TableHead>
                        <TableHead className="text-zinc-400">Emails</TableHead>
                        <TableHead className="text-zinc-400">Motivos</TableHead>
                        <TableHead className="text-zinc-400">Último intento</TableHead>
                        <TableHead className="text-right text-zinc-400">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loginFailures?.by_source?.length ? (
                        loginFailures.by_source.map((item, idx) => (
                          <TableRow key={`${item.ip_address}-${idx}`} className="border-zinc-800">
                            <TableCell className="text-white font-mono text-sm">{item.ip_address}</TableCell>
                            <TableCell className="text-zinc-400">{item.country || 'unknown'}</TableCell>
                            <TableCell className="text-amber-300 font-semibold">{item.count}</TableCell>
                            <TableCell className="text-zinc-500 text-xs max-w-[240px] truncate" title={(item.emails || []).join(', ')}>
                              {(item.emails || []).join(', ') || '-'}
                            </TableCell>
                            <TableCell className="text-zinc-500 text-xs">{(item.reasons || []).join(', ') || '-'}</TableCell>
                            <TableCell className="text-zinc-500">{formatDate(item.last_attempt)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleBlockedIp(item)}
                                disabled={item.ip_address === 'unknown' || ipActionLoading === item.ip_address}
                                className={item.is_blocked
                                  ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                                  : 'border-red-500/40 text-red-300 hover:bg-red-500/10'}
                                title={item.is_blocked ? 'Desbloquear IP' : 'Bloquear IP'}
                              >
                                {ipActionLoading === item.ip_address ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : item.is_blocked ? (
                                  <UserCheck className="h-4 w-4 mr-2" />
                                ) : (
                                  <UserX className="h-4 w-4 mr-2" />
                                )}
                                {item.is_blocked ? 'Desbloquear IP' : 'Bloquear IP'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                            No hay intentos fallidos registrados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="text-sm font-medium text-zinc-300 mb-3">Últimos intentos fallidos</p>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead className="text-zinc-400">Email</TableHead>
                          <TableHead className="text-zinc-400">IP</TableHead>
                          <TableHead className="text-zinc-400">País</TableHead>
                          <TableHead className="text-zinc-400">Motivo</TableHead>
                          <TableHead className="text-zinc-400">Fecha/Hora</TableHead>
                          <TableHead className="text-right text-zinc-400">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loginFailures?.recent?.length ? (
                          loginFailures.recent.map((log, idx) => (
                            <TableRow key={log.id || idx} className="border-zinc-800">
                              <TableCell className="text-white">{log.email || log.user_email}</TableCell>
                              <TableCell className="text-zinc-500 font-mono text-xs">{log.ip_address || 'unknown'}</TableCell>
                              <TableCell className="text-zinc-500">{log.country || 'unknown'}</TableCell>
                              <TableCell className="text-zinc-500">{log.reason || '-'}</TableCell>
                              <TableCell className="text-zinc-500">{formatDate(log.timestamp)}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteLoginFailure(log)}
                                  disabled={!log.id || loginFailureActionLoading === log.id}
                                  className="text-red-300 hover:bg-red-500/10 hover:text-red-200"
                                  title="Borrar intento"
                                >
                                  {loginFailureActionLoading === log.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                              No hay intentos fallidos registrados
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800 xl:col-span-3">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Database className="h-5 w-5 text-violet-400" />
                    Backups
                  </CardTitle>
                  <Button onClick={handleCreateBackup} disabled={backupLoading} className="bg-violet-600 hover:bg-violet-700">
                    {backupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Crear backup manual
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead className="text-zinc-400">Archivo</TableHead>
                        <TableHead className="text-zinc-400">Tamaño</TableHead>
                        <TableHead className="text-zinc-400">Creado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backups.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-zinc-500 py-8">No hay backups registrados</TableCell>
                        </TableRow>
                      ) : backups.map((backup) => (
                        <TableRow key={backup.filename} className="border-zinc-800">
                          <TableCell className="text-white font-mono text-sm">{backup.filename}</TableCell>
                          <TableCell className="text-zinc-400">{formatBytes(backup.size_bytes)}</TableCell>
                          <TableCell className="text-zinc-500">{formatDate(backup.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="h-5 w-5 text-violet-400" />
                  Sistema de Invitaciones
                </CardTitle>
                <Button onClick={() => setNewCodeDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Código
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Settings */}
                <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 space-y-4">
                  <h4 className="font-medium text-white">Configuración de Registro</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-300">Registro Abierto</p>
                      <p className="text-xs text-zinc-500">Permitir que nuevos usuarios se registren</p>
                    </div>
                    <Switch
                      checked={systemSettings.registration_open}
                      onCheckedChange={(v) => handleUpdateSettings('registration_open', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-300">Requerir Código de Invitación</p>
                      <p className="text-xs text-zinc-500">Los usuarios necesitan un código válido para registrarse</p>
                    </div>
                    <Switch
                      checked={systemSettings.require_invitation}
                      onCheckedChange={(v) => handleUpdateSettings('require_invitation', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-300">Permitir reset financiero del usuario</p>
                      <p className="text-xs text-zinc-500">Permite que cada usuario reinicie sus propios datos financieros desde Configuración</p>
                    </div>
                    <Switch
                      checked={systemSettings.allow_user_financial_reset !== false}
                      onCheckedChange={(v) => handleUpdateSettings('allow_user_financial_reset', v)}
                      data-testid="allow-user-financial-reset-toggle"
                    />
                  </div>
                  {systemSettings.require_invitation && !systemSettings.registration_open && (
                    <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      El registro está cerrado. Actívalo para usar los códigos de invitación.
                    </div>
                  )}
                </div>

                {/* Codes Table */}
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Código</TableHead>
                      <TableHead className="text-zinc-400">Usos</TableHead>
                      <TableHead className="text-zinc-400">Estado</TableHead>
                      <TableHead className="text-zinc-400">Expira</TableHead>
                      <TableHead className="text-zinc-400">Nota</TableHead>
                      <TableHead className="text-zinc-400">Usados por</TableHead>
                      <TableHead className="text-zinc-400">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitationCodes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-zinc-500 py-8">
                          No hay códigos de invitación. Crea uno nuevo.
                        </TableCell>
                      </TableRow>
                    ) : (
                      invitationCodes.map((code) => (
                        <TableRow key={code.id} className="border-zinc-800">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="px-2 py-1 bg-zinc-800 rounded text-violet-400 font-mono">{code.code}</code>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => copyToClipboard(code.code)}
                              >
                                {copiedCode === code.code ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-zinc-400" />}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-400">{code.used_count} / {code.max_uses}</TableCell>
                          <TableCell>
                            <Badge className={code.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}>
                              {code.is_active ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {code.expires_at ? formatDate(code.expires_at) : 'Nunca'}
                          </TableCell>
                          <TableCell className="text-zinc-400 text-sm max-w-32 truncate">{code.note || '-'}</TableCell>
                          <TableCell className="text-zinc-500 text-xs max-w-48">
                            {code.used_by?.length > 0 ? code.used_by.join(', ') : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className={code.is_active ? 'text-amber-400' : 'text-green-400'}
                                onClick={() => handleToggleCode(code.id)}
                              >
                                {code.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => handleDeleteCode(code.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="users-table-card">
              <CardHeader>
                <CardTitle className="text-white">Usuarios Registrados</CardTitle>
                <p className="text-sm text-zinc-400">
                  Bloquear un usuario impide nuevos inicios de sesión. Los usuarios admin quedan protegidos para evitar bloqueo accidental.
                </p>
              </CardHeader>
              <CardContent>
                {userActionMessage && (
                  <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
                    {userActionMessage}
                  </div>
                )}
                {userActionError && (
                  <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {userActionError}
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Nombre</TableHead>
                      <TableHead className="text-zinc-400">Email</TableHead>
                      <TableHead className="text-zinc-400">Rol</TableHead>
                      <TableHead className="text-zinc-400">Estado</TableHead>
                      <TableHead className="text-zinc-400">Última Acción</TableHead>
                      <TableHead className="text-zinc-400">Transacciones</TableHead>
                      <TableHead className="text-zinc-400">Datos</TableHead>
                      <TableHead className="text-zinc-400">Idioma</TableHead>
                      <TableHead className="text-zinc-400">Último Acceso</TableHead>
                      <TableHead className="text-zinc-400">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-zinc-800" data-testid={`user-row-${u.id}`}>
                        <TableCell className="text-white font-medium">{u.name}</TableCell>
                        <TableCell className="text-zinc-400">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className={u.role === 'admin' ? 'bg-violet-500/20 text-violet-400' : ''}>
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.is_active ? 'outline' : 'destructive'} className={u.is_active ? 'border-emerald-500/50 text-emerald-400' : ''}>
                            {u.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-500 text-sm max-w-[220px]">
                          {u.status_updated_at ? (
                            <div>
                              <p>{formatDate(u.status_updated_at)}</p>
                              {u.status_reason && (
                                <p className="text-zinc-400 truncate" title={u.status_reason}>
                                  {u.status_reason}
                                </p>
                              )}
                            </div>
                          ) : (
                            'Sin cambios'
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-400">{u.transaction_count || 0}</TableCell>
                        <TableCell className="text-zinc-500 text-xs">
                          <p>Cuentas: {u.account_count || 0}</p>
                          <p>Metas: {u.goal_count || 0} · Deudas: {u.debt_count || 0}</p>
                          <p>Rec.: {u.recurring_count || 0} · Notif.: {u.notification_channels_count || 0}</p>
                        </TableCell>
                        <TableCell className="text-zinc-500 text-sm">
                          <p>{u.locale || '-'}</p>
                          <p>{u.currency || '-'}</p>
                        </TableCell>
                        <TableCell className="text-zinc-500 text-sm">{formatDate(u.last_login)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-zinc-400 hover:text-white"
                              onClick={() => {
                                setSelectedUser(u);
                                setDetailDialogOpen(true);
                              }}
                              data-testid={`view-user-${u.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {u.role !== 'admin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className={u.is_active
                                  ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'
                                  : 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'}
                                onClick={() => openStatusDialog(u)}
                                disabled={statusActionUserId === u.id}
                                title={u.is_active ? 'Bloquear usuario' : 'Desbloquear usuario'}
                                data-testid={`toggle-user-${u.id}`}
                              >
                                {statusActionUserId === u.id ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : u.is_active ? (
                                  <UserX className="h-4 w-4 mr-2" />
                                ) : (
                                  <UserCheck className="h-4 w-4 mr-2" />
                                )}
                                {u.is_active ? 'Bloquear' : 'Desbloquear'}
                              </Button>
                            )}
                            {u.role === 'admin' && (
                              <span className="text-xs text-zinc-500 px-2">Admin protegido</span>
                            )}
                            {u.role !== 'admin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                                onClick={() => openResetDialog(u)}
                                title="Reiniciar datos financieros"
                                data-testid={`reset-financial-user-${u.id}`}
                              >
                                <Database className="h-4 w-4 mr-2" />
                                Reset
                              </Button>
                            )}
                            {u.role !== 'admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => handleDeleteUser(u.id, u.email)}
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="audit-table-card">
              <CardHeader>
                <CardTitle className="text-white">Auditoría Administrativa</CardTitle>
                <p className="text-sm text-zinc-400">Acciones sensibles realizadas por administradores o por reset financiero del usuario.</p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Administrador</TableHead>
                      <TableHead className="text-zinc-400">Acción</TableHead>
                      <TableHead className="text-zinc-400">Objetivo</TableHead>
                      <TableHead className="text-zinc-400">IP</TableHead>
                      <TableHead className="text-zinc-400">Fecha/Hora</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-zinc-500 py-8">No hay auditoría registrada</TableCell>
                      </TableRow>
                    ) : auditLogs.map((log, idx) => (
                      <TableRow key={log.id || idx} className="border-zinc-800">
                        <TableCell className="text-white">{log.user_email || log.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-violet-500/50 text-violet-300">{log.action}</Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400 text-sm">
                          {log.target_user_email || log.code || log.filename || '-'}
                        </TableCell>
                        <TableCell className="text-zinc-500">{log.ip_address || 'Sin IP'}</TableCell>
                        <TableCell className="text-zinc-500">{formatDate(log.timestamp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="imports">
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="imports-table-card">
              <CardHeader>
                <CardTitle className="text-white">Monitor de Importaciones</CardTitle>
                <p className="text-sm text-zinc-400">Resumen de importaciones confirmadas desde extractos bancarios o facturas de tarjeta.</p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Usuario</TableHead>
                      <TableHead className="text-zinc-400">Banco</TableHead>
                      <TableHead className="text-zinc-400">Tipo</TableHead>
                      <TableHead className="text-zinc-400">Transacciones</TableHead>
                      <TableHead className="text-zinc-400">Total</TableHead>
                      <TableHead className="text-zinc-400">Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-zinc-500 py-8">No hay importaciones registradas</TableCell>
                      </TableRow>
                    ) : importLogs.map((item, idx) => (
                      <TableRow key={`${item.import_session_id || 'import'}-${idx}`} className="border-zinc-800">
                        <TableCell>
                          <p className="text-white">{item.user_name || '-'}</p>
                          <p className="text-xs text-zinc-500">{item.user_email || item.user_id}</p>
                        </TableCell>
                        <TableCell className="text-zinc-400">{item.bank_profile}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-sky-500/50 text-sky-300">{item.import_kind}</Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400">{item.transaction_count}</TableCell>
                        <TableCell className="text-zinc-400">{Number(item.total_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                        <TableCell className="text-zinc-500">{formatDate(item.imported_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logs">
            <Card className="bg-zinc-900/50 border-zinc-800" data-testid="logs-table-card">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-white">Registro de Accesos</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAccessLogs}
                  disabled={accessLogs.length === 0 || logActionLoading === 'all'}
                  className="border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                  title="Limpiar registros"
                >
                  {logActionLoading === 'all' ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Limpiar registros
                </Button>
              </CardHeader>
              <CardContent>
                {logActionMessage && (
                  <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
                    {logActionMessage}
                  </div>
                )}
                {logActionError && (
                  <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {logActionError}
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Usuario</TableHead>
                      <TableHead className="text-zinc-400">Acción</TableHead>
                      <TableHead className="text-zinc-400">IP</TableHead>
                      <TableHead className="text-zinc-400">Fecha/Hora</TableHead>
                      <TableHead className="text-right text-zinc-400">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessLogs.map((log, idx) => (
                      <TableRow key={log.id || idx} className="border-zinc-800" data-testid={`log-row-${idx}`}>
                        <TableCell className="text-white">{log.user_email || log.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-blue-500/50 text-blue-400">
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-500">{log.ip_address || 'Sin IP'}</TableCell>
                        <TableCell className="text-zinc-500">{formatDate(log.timestamp)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAccessLog(log)}
                            disabled={!log.id || logActionLoading === log.id}
                            className="text-red-300 hover:bg-red-500/10 hover:text-red-200"
                            title="Eliminar registro"
                          >
                            {logActionLoading === log.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {accessLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-zinc-500 py-8">
                          No hay registros de acceso
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Financial Reset Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Reiniciar datos financieros</DialogTitle>
            </DialogHeader>
            {resetTargetUser && (
              <div className="space-y-4">
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="flex gap-2 text-amber-200">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p className="text-sm">
                      Esta acción borra cuentas, transacciones, presupuestos, metas, deudas, recurrentes, recibos y registros financieros del usuario. La cuenta de acceso se conserva.
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                  <p className="text-sm text-zinc-400">Usuario</p>
                  <p className="text-white font-medium">{resetTargetUser.name}</p>
                  <p className="text-xs text-zinc-500">{resetTargetUser.email}</p>
                </div>
                <div>
                  <Label htmlFor="reset-confirmation" className="text-zinc-300">
                    Escribe RESET {resetTargetUser.email}
                  </Label>
                  <Input
                    id="reset-confirmation"
                    value={resetConfirmation}
                    onChange={(e) => setResetConfirmation(e.target.value)}
                    className="mt-2 bg-zinc-800 border-zinc-700 text-white"
                    placeholder={`RESET ${resetTargetUser.email}`}
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="ghost"
                    className="text-zinc-300 hover:text-white"
                    onClick={() => setResetDialogOpen(false)}
                    disabled={resetLoading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="bg-sky-600 hover:bg-sky-700"
                    onClick={handleResetFinancialData}
                    disabled={resetLoading || resetConfirmation !== `RESET ${resetTargetUser.email}`}
                  >
                    {resetLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Reiniciar
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Status Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">
                {statusTargetUser?.is_active ? 'Bloquear usuario' : 'Desbloquear usuario'}
              </DialogTitle>
            </DialogHeader>
            {statusTargetUser && (
              <div className="space-y-4">
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                  <p className="text-sm text-zinc-400">Usuario</p>
                  <p className="text-white font-medium">{statusTargetUser.name}</p>
                  <p className="text-xs text-zinc-500">{statusTargetUser.email}</p>
                </div>
                <p className="text-sm text-zinc-400">
                  {statusTargetUser.is_active
                    ? 'Este usuario no podrá iniciar sesión mientras esté inactivo.'
                    : 'Este usuario podrá iniciar sesión nuevamente después de desbloquearlo.'}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="status-reason" className="text-zinc-300">
                    Motivo o nota
                  </Label>
                  <Input
                    id="status-reason"
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    placeholder={statusTargetUser.is_active ? 'Ej. pago pendiente, uso indebido...' : 'Ej. pago regularizado, revisión aprobada...'}
                    className="bg-zinc-950 border-zinc-800 focus:border-violet-500"
                    maxLength={180}
                    data-testid="status-reason-input"
                  />
                  <p className="text-xs text-zinc-500">{statusReason.length}/180</p>
                </div>
                <DialogFooter>
                  <Button
                    variant="ghost"
                    className="text-zinc-400 hover:text-white"
                    onClick={() => setStatusDialogOpen(false)}
                    disabled={statusActionUserId === statusTargetUser.id}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className={statusTargetUser.is_active ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                    onClick={handleToggleUserStatus}
                    disabled={statusActionUserId === statusTargetUser.id}
                    data-testid="confirm-status-change-btn"
                  >
                    {statusActionUserId === statusTargetUser.id && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {statusTargetUser.is_active ? 'Bloquear usuario' : 'Desbloquear usuario'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Detalles del Usuario</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-500 text-sm">Nombre</p>
                    <p className="text-white font-medium">{selectedUser.name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Email</p>
                    <p className="text-white">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Rol</p>
                    <Badge className={selectedUser.role === 'admin' ? 'bg-violet-500/20 text-violet-400' : ''}>
                      {selectedUser.role}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Estado</p>
                    <Badge className={selectedUser.is_active ? 'border-emerald-500/50 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                      {selectedUser.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Verificado</p>
                    <p className="text-white">{selectedUser.is_verified ? 'Sí' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Transacciones</p>
                    <p className="text-white">{selectedUser.transaction_count || 0}</p>
                  </div>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Fecha de Registro</p>
                  <p className="text-white">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div>
                  <p className="text-zinc-500 text-sm">Último Acceso</p>
                  <p className="text-white">{formatDate(selectedUser.last_login)}</p>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
                  <p className="text-zinc-500 text-sm mb-2">Auditoría de bloqueo</p>
                  {selectedUser.status_updated_at ? (
                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-zinc-500">Última modificación de estado</p>
                        <p className="text-white">{formatDate(selectedUser.status_updated_at)}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Responsable</p>
                        <p className="text-white">{selectedUser.status_updated_by_admin_email || selectedUser.status_updated_by_admin_id || 'No registrado'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Motivo</p>
                        <p className="text-white whitespace-pre-wrap">{selectedUser.status_reason || 'Sin motivo informado'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500">Sin bloqueos o desbloqueos registrados.</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* New Invitation Code Dialog */}
        <Dialog open={newCodeDialogOpen} onOpenChange={setNewCodeDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Código de Invitación</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCode} className="space-y-4">
              <div>
                <Label className="text-zinc-300">Código (opcional)</Label>
                <Input
                  value={newCodeForm.code}
                  onChange={(e) => setNewCodeForm({...newCodeForm, code: e.target.value.toUpperCase()})}
                  placeholder="Dejar vacío para auto-generar"
                  className="bg-zinc-800 border-zinc-700 font-mono"
                  maxLength={12}
                />
              </div>
              <div>
                <Label className="text-zinc-300">Máximo de usos</Label>
                <Input
                  type="number"
                  value={newCodeForm.max_uses}
                  onChange={(e) => setNewCodeForm({...newCodeForm, max_uses: e.target.value})}
                  className="bg-zinc-800 border-zinc-700"
                  min={1}
                  required
                />
              </div>
              <div>
                <Label className="text-zinc-300">Expira en (días)</Label>
                <Input
                  type="number"
                  value={newCodeForm.expires_days}
                  onChange={(e) => setNewCodeForm({...newCodeForm, expires_days: e.target.value})}
                  placeholder="Dejar vacío para nunca expirar"
                  className="bg-zinc-800 border-zinc-700"
                  min={1}
                />
              </div>
              <div>
                <Label className="text-zinc-300">Nota (opcional)</Label>
                <Input
                  value={newCodeForm.note}
                  onChange={(e) => setNewCodeForm({...newCodeForm, note: e.target.value})}
                  placeholder="Ej: Para cliente VIP"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setNewCodeDialogOpen(false)} className="border-zinc-700">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700">
                  Crear Código
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminPage;
